import { useState, useRef, useCallback } from "react";
import * as maplibregl from "maplibre-gl";
import type { FeatureCollection, Polygon, MultiPolygon } from "geojson";
import {
  MAP_STYLES,
  CATEGORY_COLORS,
  ICON_NAMES,
  MAP_CONFIG,
  OUTDOOR_ENTRANCE_ROTATIONS,
} from "../config/constants";
import {
  calculateMapConfig,
  createLabelPoints,
  calculatePaddedBounds,
  transformGeoJSONCoords,
} from "../utils/mapUtils";
import { bbox, center } from "@turf/turf";
import { bizUdpGothicFontFamily } from "../styles/fonts";
import type { MapLabelLanguage } from "@/utils/mapDataUtils";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

// maplibre-gl v6 はバンドラ経由だとワーカー URL を自動解決できないため明示する (公式の Vite 向け手順)
maplibregl.setWorkerUrl(maplibreWorkerUrl);

const GLYPHS_URL =
  import.meta.env.VITE_GLYPHS_URL ?? "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf";

const createMapStyle: () => maplibregl.StyleSpecification = () => ({
  version: 8 as const,
  glyphs: GLYPHS_URL,
  sources: {},
  layers: [
    {
      id: "background",
      type: "background" as const,
      paint: { "background-color": MAP_STYLES.background },
    },
  ],
});

const addDataSources = (
  mapInstance: maplibregl.Map,
  polygons: FeatureCollection,
  icons: FeatureCollection,
  labelPoints: FeatureCollection,
  doorGaps: FeatureCollection,
) => {
  mapInstance.addSource("floor-polygons", {
    type: "geojson",
    data: polygons,
  });
  mapInstance.addSource("floor-icons", {
    type: "geojson",
    data: icons,
  });
  mapInstance.addSource("floor-labels", {
    type: "geojson",
    data: labelPoints,
  });
  mapInstance.addSource("door-gaps", {
    type: "geojson",
    data: doorGaps,
  });
};

const addMapLayers = (mapInstance: maplibregl.Map) => {
  mapInstance.addLayer({
    id: "rooms-fill",
    type: "fill",
    source: "floor-polygons",
    // STAR 形状は図形を非表示にする
    filter: ["!=", ["get", "shapeType"], "STAR"],
    paint: {
      "fill-color":
        buildCategoryFillColorExpression() as unknown as maplibregl.PropertyValueSpecification<maplibregl.ColorSpecification>,
      "fill-opacity": 0.85,
    },
  });

  mapInstance.addLayer({
    id: "rooms-outline",
    type: "line",
    source: "floor-polygons",
    // STAR 形状は図形を非表示にする
    filter: ["!=", ["get", "shapeType"], "STAR"],
    paint: {
      "line-color": MAP_STYLES.roomOutlineColor,
      "line-width": MAP_STYLES.roomOutlineWidth,
    },
  });

  // 階段のハッチ線（LineString）を描画するための専用レイヤ
  // properties.category === 'stairs-hatch' のフィーチャを対象にする
  mapInstance.addLayer({
    id: "stairs-hatch",
    type: "line",
    source: "floor-polygons",
    filter: ["==", ["get", "category"], "stairs-hatch"],
    layout: {
      "line-cap": "butt",
      "line-join": "miter",
    },
    paint: {
      "line-color": MAP_STYLES.stairsLineColor,
      "line-width": MAP_STYLES.roomOutlineWidth,
      "line-opacity": 0.95,
    },
  });

  // ラベル用レイヤーの共通設定
  const baseLabelLayout: maplibregl.SymbolLayerSpecification["layout"] = {
    "text-field": [
      "coalesce",
      ["get", "displayRoom"],
      ["get", "room"],
      ["get", "id"],
    ] as unknown as maplibregl.PropertyValueSpecification<string>,
    "text-font": ["Open Sans Regular"],
    "text-letter-spacing": 0.06,
    "text-line-height": 1.5,
    "text-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      MAP_CONFIG.minZoom,
      ["*", ["coalesce", ["get", "labelTextSize"], 14], 0.92],
      MAP_CONFIG.maxZoom,
      ["coalesce", ["get", "labelTextSize"], 14],
    ] as unknown as maplibregl.PropertyValueSpecification<number>,
    "text-allow-overlap": false,
    "text-max-width": 20,
    "symbol-placement": "point",
    "symbol-avoid-edges": true,
    "text-padding": 2,
    "text-optional": true,
  };

  // ラベルの描画設定_共通部分
  const baseLabelPaint: maplibregl.SymbolLayerSpecification["paint"] = {
    "text-color": "#23221F",
    "text-halo-color": "#ffffff",
    "text-halo-width": 1.5,
  };

  // 通常ラベル用レイヤー
  mapInstance.addLayer({
    id: "door-gap-mask",
    type: "line",
    source: "door-gaps",
    layout: {
      "line-cap": "butt",
      "line-join": "miter",
    },
    paint: {
      "line-color": MAP_STYLES.baseColor,
      "line-width": MAP_STYLES.roomOutlineWidth + 1,
    },
  });

  mapInstance.addLayer({
    id: "rooms-labels",
    type: "symbol",
    source: "floor-labels",
    minzoom: MAP_CONFIG.minZoom,
    filter: ["any", ["!", ["has", "isLowPriority"]], ["==", ["get", "isLowPriority"], false]],
    layout: baseLabelLayout,
    paint: baseLabelPaint,
  });

  // 低優先度ラベル用レイヤー
  mapInstance.addLayer({
    id: "rooms-labels-low",
    type: "symbol",
    source: "floor-labels",
    minzoom: 18,
    filter: ["==", ["get", "isLowPriority"], true],
    layout: baseLabelLayout,
    paint: baseLabelPaint,
  });

  const otherIconNames = ICON_NAMES.filter(
    (name) =>
      name !== "indoor_entrance" &&
      name !== "outdoor_entrance" &&
      name !== "emergency_exit" &&
      name !== "AED",
  );
  const otherIconMatchExpression = otherIconNames.flatMap((iconName) => [
    iconName,
    `${iconName}-icon`,
  ]);
  const iconOverlapReleaseZoom = (MAP_CONFIG.iconMinZoom ?? 16) + 0.75;
  const iconOverlapControlExpression = [
    "step",
    ["zoom"],
    false,
    iconOverlapReleaseZoom,
    true,
  ] as unknown as maplibregl.PropertyValueSpecification<boolean>;

  mapInstance.addLayer({
    id: "other-icons",
    type: "symbol",
    source: "floor-icons",
    filter: ["in", ["get", "category"], ["literal", otherIconNames]],
    layout: {
      "icon-image": [
        "match",
        ["get", "category"],
        ...otherIconMatchExpression,
        "",
      ] as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>,
      "icon-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        14,
        0.8,
        16,
        1,
        19,
        1.2,
      ] as unknown as maplibregl.PropertyValueSpecification<number>,
      "icon-allow-overlap": iconOverlapControlExpression,
      "icon-ignore-placement": iconOverlapControlExpression,
      "icon-anchor": "center",
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
    },
  });

  // 屋外入口（矢印）用レイヤー
  mapInstance.addLayer({
    id: "outdoor-entrance-icons",
    type: "symbol",
    source: "floor-icons",
    filter: ["==", ["get", "category"], "outdoor_entrance"],
    layout: {
      "icon-image": "outdoor_entrance-icon",
      "icon-size": ["interpolate", ["linear"], ["zoom"], 14, 0.8, 16, 1, 19, 1.2],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-anchor": "center",
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "map",
      "icon-rotate": ["get", "rotation"],
    },
  });

  // 非常口・AEDアイコン用レイヤー (ズームレベル18以上でのみ表示)
  mapInstance.addLayer({
    id: "emergency-exit-icons",
    type: "symbol",
    source: "floor-icons",
    minzoom: 18,
    filter: ["in", ["get", "category"], ["literal", ["emergency_exit", "AED"]]],
    layout: {
      "icon-image": [
        "match",
        ["get", "category"],
        "emergency_exit",
        "emergency_exit-icon",
        "AED",
        "AED-icon",
        "",
      ],
      "icon-size": ["interpolate", ["linear"], ["zoom"], 18, 0.8, 20, 1],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-anchor": "center",
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
    },
  });
};

const getBuildingPointerTextField = (
  language: MapLabelLanguage,
): maplibregl.PropertyValueSpecification<string> =>
  (language === "en"
    ? ["coalesce", ["get", "label_en"], ["get", "label"]]
    : [
        "coalesce",
        ["get", "label"],
        ["get", "label_en"],
      ]) as maplibregl.PropertyValueSpecification<string>;

const addBuildingPointerLayers = (mapInstance: maplibregl.Map, language: MapLabelLanguage) => {
  // テキストレイヤーのみ
  mapInstance.addLayer({
    id: "building-pointer-label",
    type: "symbol",
    source: "building-pointers",
    minzoom: 0,
    layout: {
      "text-field": getBuildingPointerTextField(language),
      "text-font": ["Open Sans Regular"],
      "text-letter-spacing": 0.06,
      "text-line-height": 1.5,
      "text-size": 14,
      "text-anchor": "center",
      "text-offset": [0, 0],
      "text-allow-overlap": false,
      "text-ignore-placement": false,
      "text-rotation-alignment": "viewport",
      "text-keep-upright": true,
    },
    paint: {
      "text-color": "#23221f",
      "text-opacity": 1,
      "text-halo-color": "#ffffff",
      "text-halo-width": 3,
      "text-halo-blur": 1,
    },
  });
};

const updateBuildingPointerVisibility = (mapInstance: maplibregl.Map, floorId: string) => {
  // 現在のfloorIdに一致するポインターのみ表示
  const filter = ["==", ["get", "floorId"], floorId] as maplibregl.FilterSpecification;

  if (mapInstance.getLayer("building-pointer-label")) {
    mapInstance.setFilter("building-pointer-label", filter);
  }
};

type MapWithBounds = Pick<maplibregl.Map, "setMaxBounds" | "getCanvas">;

// レスポンシブにパディング比率を計算する
const getResponsivePaddingRatios = (mapInstance: MapWithBounds) => {
  const canvas = mapInstance.getCanvas();
  const width = canvas?.clientWidth ?? 0;
  const height = canvas?.clientHeight ?? 0;

  if (!width || !height) {
    return {
      ratioX: MAP_CONFIG.panPaddingRatio,
      ratioY: MAP_CONFIG.panPaddingRatio,
    };
  }

  const aspectRatio = height / width;

  return {
    ratioX: MAP_CONFIG.panPaddingRatio,
    ratioY: MAP_CONFIG.panPaddingRatio * aspectRatio,
  };
};

const setPanningBounds = (
  mapInstance: MapWithBounds,
  polygons: FeatureCollection<Polygon | MultiPolygon>,
) => {
  const responsivePadding = getResponsivePaddingRatios(mapInstance);
  const bounds = calculatePaddedBounds(polygons, responsivePadding);
  if (!bounds) return;

  mapInstance.setMaxBounds(bounds);
};

// フロアごとに動的に最小ズームレベルを計算
const calculateMinZoomForFloor = (
  mapInstance: maplibregl.Map,
  polygons: FeatureCollection<Polygon | MultiPolygon>,
): number => {
  const [minX, minY, maxX, maxY] = bbox(polygons);

  const floorWidth = maxX - minX;
  const floorHeight = maxY - minY;

  const paddedBounds: [[number, number], [number, number]] = [
    [minX - floorWidth * 0.5, minY - floorHeight * 0.5],
    [maxX + floorWidth * 0.5, maxY + floorHeight * 0.5],
  ];

  const camera = mapInstance.cameraForBounds(paddedBounds, {
    padding: { top: 20, bottom: 20, left: 20, right: 20 },
  });

  const calculatedZoom = camera?.zoom ?? MAP_CONFIG.minZoom;

  const minZoomWithMargin = calculatedZoom - 0.5;

  return Math.max(12, Math.min(minZoomWithMargin, MAP_CONFIG.maxZoom));
};

const buildCategoryFillColorExpression = () => {
  const expression: unknown[] = ["case", ["==", ["get", "id"], "base"], MAP_STYLES.baseColor];

  // 各カラーグループについて、そのグループに含まれる全カテゴリの条件を追加
  CATEGORY_COLORS.forEach(({ color, categories }) => {
    categories.forEach((category) => {
      expression.push(["==", ["coalesce", ["get", "category"], ""], category]);
      expression.push(color);
    });
  });

  // カテゴリ未設定の場合のデフォルト色
  expression.push(MAP_STYLES.defaultRoomFill);

  return expression as maplibregl.ExpressionSpecification;
};

const loadIcon = async (mapInstance: maplibregl.Map, iconNameList: string[]) => {
  await Promise.all(iconNameList.map((iconName) => ensureIcon(mapInstance, iconName)));
};

const ensureIcon = async (mapInstance: maplibregl.Map, iconName: string) => {
  if (mapInstance.hasImage(`${iconName}-icon`)) {
    return;
  }

  try {
    const icon = await loadOneIcon(`/icons/${iconName}.png`);

    if (mapInstance.hasImage(`${iconName}-icon`)) {
      return;
    }

    mapInstance.addImage(`${iconName}-icon`, icon, { pixelRatio: 2 });
  } catch (error) {
    console.error(`Failed to register ${iconName} icon`, error);
  }
};

const loadOneIcon = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
  });
};

const injectIconRotation = (icons: FeatureCollection): FeatureCollection => {
  const { scaleFactor } = calculateMapConfig();

  const newFeatures = icons.features.map((feature) => {
    if (feature.properties?.category !== "outdoor_entrance") {
      return feature;
    }
    if (feature.geometry.type !== "Point") {
      return feature;
    }

    const [x, y] = feature.geometry.coordinates;

    const originalX = x / scaleFactor + MAP_CONFIG.minX;
    const originalY = y / scaleFactor + MAP_CONFIG.minY;

    const roundedX = Math.round(originalX * 10) / 10;
    const roundedY = Math.round(originalY * 10) / 10;

    const key = `${roundedX}:${roundedY}`;
    const rotation = OUTDOOR_ENTRANCE_ROTATIONS[key];

    if (rotation !== undefined) {
      return {
        ...feature,
        properties: {
          ...feature.properties,
          rotation,
        },
      };
    }
    return feature;
  });

  return {
    ...icons,
    features: newFeatures,
  };
};

export const useMapInitialization = () => {
  const map = useRef<maplibregl.Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const initializeMap = useCallback(
    (
      container: HTMLDivElement,
      polygons: FeatureCollection,
      icons: FeatureCollection,
      doorGaps: FeatureCollection,
      buildingPointerLanguage: MapLabelLanguage = "ja",
    ) => {
      if (map.current || !container) return;
      const { scaledCenter } = calculateMapConfig();

      // レスポンシブにモバイル用の min/max を適用する
      const isMobile =
        typeof window !== "undefined" && window.innerWidth < (MAP_CONFIG.mobileBreakpoint ?? 640);
      const effectiveMinZoom = isMobile
        ? (MAP_CONFIG.mobileMinZoom ?? MAP_CONFIG.minZoom)
        : MAP_CONFIG.minZoom;
      const effectiveMaxZoom = isMobile
        ? (MAP_CONFIG.mobileMaxZoom ?? MAP_CONFIG.maxZoom)
        : MAP_CONFIG.maxZoom;

      const configuredInitialZoom = MAP_CONFIG.initialZoom ?? effectiveMinZoom + 1;
      const initialZoom = Math.min(configuredInitialZoom, effectiveMaxZoom);

      const mapInstance = new maplibregl.Map({
        container,
        style: createMapStyle(),
        center: scaledCenter,
        zoom: initialZoom,
        minZoom: effectiveMinZoom,
        maxZoom: effectiveMaxZoom,
        pitch: isMobile ? 0 : (MAP_CONFIG.birdseyePitch ?? 0),
        bearing: 0,
        renderWorldCopies: false,
        attributionControl: false,
        // Use BIZ UDP Gothic for locally generated glyphs (CJK) so labels match the app font.
        localIdeographFontFamily: bizUdpGothicFontFamily,
      });

      map.current = mapInstance;

      mapInstance.on("load", async () => {
        if (!mapInstance) return;

        const labelPoints = createLabelPoints(
          polygons as FeatureCollection<Polygon | MultiPolygon>,
        );

        const iconsWithRotation = injectIconRotation(icons);
        addDataSources(mapInstance, polygons, iconsWithRotation, labelPoints, doorGaps);

        setPanningBounds(mapInstance, polygons as FeatureCollection<Polygon | MultiPolygon>);

        // map の maxZoom/minZoom をウィンドウ幅に応じて再設定しておく（リサイズ対応は別途）
        mapInstance.setMinZoom(
          isMobile ? (MAP_CONFIG.mobileMinZoom ?? MAP_CONFIG.minZoom) : MAP_CONFIG.minZoom,
        );
        mapInstance.setMaxZoom(
          isMobile ? (MAP_CONFIG.mobileMaxZoom ?? MAP_CONFIG.maxZoom) : MAP_CONFIG.maxZoom,
        );

        await loadIcon(mapInstance, ICON_NAMES);

        // ポインターデータを読み込み
        try {
          const buildingPointersResponse = await fetch("/building-pointers.geojson");
          const buildingPointersData = await buildingPointersResponse.json();

          const { scaleFactor } = calculateMapConfig();
          const scaledBuildingPointers = transformGeoJSONCoords(buildingPointersData, scaleFactor);

          mapInstance.addSource("building-pointers", {
            type: "geojson",
            data: scaledBuildingPointers,
          });
        } catch (error) {
          console.warn("Building pointers not loaded:", error);
        }

        addMapLayers(mapInstance);

        // 建物ポインターレイヤーを最上層に追加
        try {
          if (mapInstance.getSource("building-pointers")) {
            addBuildingPointerLayers(mapInstance, buildingPointerLanguage);
          }
        } catch (error) {
          console.warn("Building pointer layers not added:", error);
        }

        setIsMapLoaded(true);
      });
    },
    [],
  );

  const updateMapData = useCallback(
    (polygons: FeatureCollection, icons: FeatureCollection, doorGaps: FeatureCollection) => {
      if (!map.current || !isMapLoaded) return;

      const mapInstance = map.current;

      const currentCenter = mapInstance.getCenter();
      const currentZoom = mapInstance.getZoom();
      const currentBearing = mapInstance.getBearing();
      const currentPitch = mapInstance.getPitch();

      const labelPoints = createLabelPoints(polygons as FeatureCollection<Polygon | MultiPolygon>);

      const polygonSource = mapInstance.getSource("floor-polygons") as maplibregl.GeoJSONSource;
      const iconSource = mapInstance.getSource("floor-icons") as maplibregl.GeoJSONSource;
      const labelSource = mapInstance.getSource("floor-labels") as maplibregl.GeoJSONSource;
      const doorGapSource = mapInstance.getSource("door-gaps") as maplibregl.GeoJSONSource;

      if (polygonSource) {
        polygonSource.setData(polygons);
      }

      if (iconSource) {
        const iconsWithRotation = injectIconRotation(icons);
        iconSource.setData(iconsWithRotation);
      }

      if (labelSource) {
        labelSource.setData(labelPoints);
      }

      if (doorGapSource) {
        doorGapSource.setData(doorGaps);
      }

      // フロアごとに動的に最小ズームレベルを計算・設定
      const minZoom = calculateMinZoomForFloor(
        mapInstance,
        polygons as FeatureCollection<Polygon | MultiPolygon>,
      );
      const isMobile =
        typeof window !== "undefined" && window.innerWidth < (MAP_CONFIG.mobileBreakpoint ?? 640);
      const effectiveMobileMin = isMobile
        ? (MAP_CONFIG.mobileMinZoom ?? MAP_CONFIG.minZoom)
        : MAP_CONFIG.minZoom;
      mapInstance.setMinZoom(Math.max(minZoom, effectiveMobileMin));

      setPanningBounds(mapInstance, polygons as FeatureCollection<Polygon | MultiPolygon>);

      // 遷移先フロアのバウンディングボックスを取得
      const [minX, minY, maxX, maxY] = bbox(polygons);

      // 現在の座標が遷移先フロアの範囲内にあるかチェック
      const isCurrentPositionInBounds =
        currentCenter.lng >= minX &&
        currentCenter.lng <= maxX &&
        currentCenter.lat >= minY &&
        currentCenter.lat <= maxY;

      let targetCenter: [number, number];
      let targetZoom: number;

      if (isCurrentPositionInBounds) {
        // 範囲内の場合は、現在の座標とズームレベルを維持
        targetCenter = [currentCenter.lng, currentCenter.lat];
        targetZoom = currentZoom;
      } else {
        // 範囲外の場合は、フロアの中心に移動してズームレベルを設定
        const floorCenter = center(polygons);
        const [centerLng, centerLat] = floorCenter.geometry.coordinates;
        targetCenter = [centerLng, centerLat];
        targetZoom = minZoom + 1;
      }

      mapInstance.flyTo({
        center: targetCenter,
        zoom: targetZoom,
        bearing: currentBearing,
        pitch: currentPitch,
        duration: 800,
        essential: true,
      });
    },
    [isMapLoaded],
  );

  const updateBuildingPointers = useCallback(
    (floorId: string) => {
      if (!map.current || !isMapLoaded) return;
      updateBuildingPointerVisibility(map.current, floorId);
    },
    [isMapLoaded],
  );

  const updateBuildingPointerLanguage = useCallback(
    (language: MapLabelLanguage) => {
      if (!map.current || !isMapLoaded) return;
      const mapInstance = map.current;
      if (mapInstance.getLayer("building-pointer-label")) {
        mapInstance.setLayoutProperty(
          "building-pointer-label",
          "text-field",
          getBuildingPointerTextField(language),
        );
      }
    },
    [isMapLoaded],
  );

  return {
    map,
    isMapLoaded,
    initializeMap,
    updateMapData,
    updateBuildingPointers,
    updateBuildingPointerLanguage,
  };
};
